/**
 * O STATUS de uma organização — UMA função para o `trialMiddleware` (que bloqueia)
 * e para a Plataforma (que mostra). Duas leituras da mesma coluna divergindo é
 * como a lista diz "trial ativo" para uma org que a API já está recusando.
 *
 * Regras, na ordem em que decidem:
 *   ativo = false                       → INATIVA        (bloqueia: 402 SUBSCRIPTION_INACTIVE)
 *   stripeSubscriptionId presente       → ASSINANTE      (passa)
 *   trialEndsAt = NULL                  → CORTESIA       (passa — "grandfathered")
 *   trialEndsAt no futuro               → TRIAL          (passa)
 *   trialEndsAt no passado              → TRIAL_EXPIRADO (bloqueia: 402 TRIAL_EXPIRED)
 */
export type StatusOrg = 'INATIVA' | 'ASSINANTE' | 'CORTESIA' | 'TRIAL' | 'TRIAL_EXPIRADO';

export interface OrgParaStatus {
  ativo: boolean | null | undefined;
  trialEndsAt: Date | string | null | undefined;
  stripeSubscriptionId: string | null | undefined;
}

export const STATUS_ORG: readonly StatusOrg[] = ['INATIVA', 'ASSINANTE', 'CORTESIA', 'TRIAL', 'TRIAL_EXPIRADO'];

export function statusDaOrg(org: OrgParaStatus, agora: Date = new Date()): StatusOrg {
  if (org.ativo === false) return 'INATIVA';
  if (org.stripeSubscriptionId) return 'ASSINANTE';
  if (!org.trialEndsAt) return 'CORTESIA';
  const fim = org.trialEndsAt instanceof Date ? org.trialEndsAt : new Date(org.trialEndsAt);
  return fim.getTime() > agora.getTime() ? 'TRIAL' : 'TRIAL_EXPIRADO';
}

/** A org pode usar o app (não é o mesmo que "está ativa": trial vencido bloqueia). */
export function orgPodeUsar(status: StatusOrg): boolean {
  return status === 'ASSINANTE' || status === 'CORTESIA' || status === 'TRIAL';
}

/**
 * A org deve VER o caminho de assinatura — o item "Assinatura" do menu, a parede de
 * trial e qualquer CTA de "assinar"?
 *
 * Não, quando está em CORTESIA. Desde o lançamento gratuito (nov/2026) toda fazenda
 * nova nasce em cortesia, e oferecer "Assine por R$ 99/mês" a quem não paga nada é
 * contradizer a landing. Status DESCONHECIDO (`undefined`: client com o user antigo
 * no localStorage, modo suporte) mantém o comportamento de antes — mostra.
 *
 * É só o que a TELA mostra: a rota /admin/assinatura e o Stripe continuam vivos, e
 * quem bloqueia é o `trialMiddleware`, que nunca bloqueia CORTESIA.
 */
export function orgVeAssinatura(status: StatusOrg | null | undefined): boolean {
  return status !== 'CORTESIA';
}

/** Dias que faltam do trial; `null` quando não há trial a contar. */
export function diasRestantesTrial(trialEndsAt: Date | string | null | undefined, agora: Date = new Date()): number | null {
  if (!trialEndsAt) return null;
  const fim = trialEndsAt instanceof Date ? trialEndsAt : new Date(trialEndsAt);
  return Math.max(0, Math.ceil((fim.getTime() - agora.getTime()) / 86_400_000));
}

export const ROTULO_STATUS_ORG: Record<StatusOrg, string> = {
  INATIVA: 'Inativa',
  ASSINANTE: 'Assinante',
  CORTESIA: 'Cortesia',
  TRIAL: 'Trial',
  TRIAL_EXPIRADO: 'Trial vencido',
};
